import { nativeDrawable } from './renderPolicy'

/**
 * THE NATIVE DRAWABLE for a canvas (renderPolicy.ts — resolution is not a lever): its CSS size × the device
 * pixel ratio, capped only by the GPU's largest drawable. Read every frame (the window can change); the GPU's
 * limit once, from the context the host already made on the canvas (asking for the same kind returns it).
 *
 * The CSS size is the canvas's FRACTIONAL box (getBoundingClientRect), never `clientWidth`: that one is rounded
 * to a whole CSS px, and at a fractional devicePixelRatio the rounding is multiplied back up — a 1280-px
 * Android TV plane at DPR 1.333 lays the canvas out at 960.x CSS px, `clientWidth` says 961, and 961 × 1.333
 * drew 1281 px on the 1280-px screen. The fractional box × DPR, rounded once, is the screen's own pixels.
 */
export function createNativeSizer(canvas: HTMLCanvasElement): () => { w: number; h: number } {
  let maxDim: number | null = null
  const readMax = (): number => {
    try {
      const gl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) as WebGLRenderingContext | null
      if (!gl) return Infinity
      const vp = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array | null
      const rb = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number | null
      return Math.min(vp ? Math.min(vp[0], vp[1]) : Infinity, rb ?? Infinity)
    } catch { return Infinity }
  }
  return () => {
    if (maxDim === null) maxDim = readMax()
    const box = canvas.getBoundingClientRect()
    return nativeDrawable(box.width || window.innerWidth, box.height || window.innerHeight, window.devicePixelRatio || 1, maxDim)
  }
}
